import { CompareSalesResponse } from '../services/inspector/api'
import { xeroPaymentsForMatch } from './salesFormatters'
import { collectSaleMismatches, SaleMismatch } from './saleMismatchIssues'

export interface SaleSummary {
  matchedCount: number
  warningCount: number
  saleMismatches: SaleMismatch[]
}

/**
 * Pure helper: derive the chip data the compare-sales title bar needs
 * from a compare response. Mirrors the credit-aware payments_match logic
 * in SALE_MISMATCHES_CTE_BODY so the dialog and the inspector exception
 * table reach the same verdict on what's a mismatch.
 *
 * Lives in /utils so the React-component file (CompareSalesView.tsx)
 * stays Fast-Refresh-friendly - mixing component and non-component
 * exports in the same module forces a full reload on every edit.
 */
export const computeSaleSummary = (data: CompareSalesResponse): SaleSummary => {
  const { lightspeed, xero, comparison } = data
  const errorCount = comparison.error_count ?? 0
  const xeroPaymentsForMatchValue = xeroPaymentsForMatch(
    lightspeed.payments_total,
    xero.amount_paid,
    xero.amount_credited,
    !!(xero.amount_credited && xero.amount_credited > 0),
    xero.kind,
    xero.total_inc_tax,
  )
  const saleMismatches = collectSaleMismatches({
    ls: {
      date: lightspeed.sale_date,
      client: lightspeed.customer_name,
      status: lightspeed.status,
      amount_inc_tax: lightspeed.total_inc_tax,
      amount_ex_tax: lightspeed.total_ex_tax,
      tax: lightspeed.total_tax,
      payments_total: lightspeed.payments_total ?? 0,
    },
    xero: {
      date: xero.invoice_date,
      client: xero.contact_name,
      status: xero.status,
      amount_inc_tax: xero.total_inc_tax,
      amount_ex_tax: xero.total_ex_tax,
      tax: xero.total_tax,
      payments_total: xeroPaymentsForMatchValue,
    },
    lineItemsMismatch: errorCount > 0,
    lsLineCount: lightspeed.count,
    xeroLineCount: xero.count,
  })
  return {
    matchedCount: comparison.matched_count ?? 0,
    warningCount: comparison.warning_count ?? 0,
    saleMismatches,
  }
}
